// src/components/OSINT/LeakTrace.js
//
// Horizontal node-timeline for one account's leak provenance. Each node is a
// distinct leak EVENT (deduplicated across rehosted copies), placed
// chronologically and colored by lifecycle stage. Reads left-to-right as the
// propagation chain: infostealer -> breach -> combolist -> paste -> forum.

import React from 'react';
import { ExternalLink, Crosshair } from 'lucide-react';

// Lifecycle stage -> visual config. Mirrors osint_trace.STAGE_LABELS.
const STAGE_CONFIG = {
  infostealer: { label: 'Infostealer log',     color: '#ef4444' },
  breach_dump: { label: 'Data breach',         color: '#f97316' },
  combolist:   { label: 'Credential combolist', color: '#eab308' },
  paste:       { label: 'Paste site',          color: '#3b82f6' },
  forum:       { label: 'Forum / darknet',     color: '#a855f7' },
  lead_gen:    { label: 'B2B lead-gen scrape', color: '#64748b' },
  whois:       { label: 'WHOIS dump',          color: '#64748b' },
  other:       { label: 'Other source',        color: '#94a3b8' },
};

const SEVERITY_PILL = {
  critical:  'text-red-700 bg-red-100 border-red-300',
  suspected: 'text-rose-700 bg-rose-100 border-rose-300',
  high:     'text-orange-700 bg-orange-100 border-orange-300',
  medium:   'text-yellow-700 bg-yellow-100 border-yellow-300',
  low:      'text-blue-700 bg-blue-100 border-blue-300',
  info:     'text-slate-700 bg-slate-100 border-slate-300',
  noise:    'text-slate-500 bg-slate-100 border-slate-200',
};

const fmtDate = (iso) => {
  if (!iso) return 'undated';
  try {
    return new Date(iso).toISOString().slice(0, 10);
  } catch {
    return iso;
  }
};

const LeakTrace = ({ nodes }) => {
  if (!nodes || nodes.length === 0) {
    return (
      <div className="rounded-xl border border-cyber-300/40 bg-cyber-100/20 p-8 text-center">
        <p className="text-sm text-slate-500">No breach sources on the timeline yet.</p>
        <p className="text-xs text-slate-500 mt-1">
          Run a deep search on this account to populate its provenance trace.
        </p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto pb-2">
      <div className="flex min-w-max px-2">
        {nodes.map((n, i) => {
          const stage = STAGE_CONFIG[n.stage] || STAGE_CONFIG.other;
          const isFirst = i === 0;
          const isLast = i === nodes.length - 1;
          return (
            <div key={n.finding_id} className="flex flex-col items-center" style={{ width: 196 }}>
              {/* Date */}
              <div className="h-5 text-[11px] font-mono text-slate-500">{fmtDate(n.date)}</div>

              {/* Rail: connecting line + stage dot */}
              <div className="relative w-full h-8 flex items-center justify-center">
                {!isFirst && (
                  <div
                    className="absolute left-0 w-1/2 h-0.5 bg-cyber-300/50"
                    style={{ top: '50%', transform: 'translateY(-50%)' }}
                  />
                )}
                {!isLast && (
                  <div
                    className="absolute right-0 w-1/2 h-0.5 bg-cyber-300/50"
                    style={{ top: '50%', transform: 'translateY(-50%)' }}
                  />
                )}
                <div
                  className="relative z-10 w-4 h-4 rounded-full"
                  style={{
                    background: stage.color,
                    boxShadow: 'none',
                    border: n.is_origin ? '2px solid #fca5a5' : '2px solid rgba(6,31,51,0.9)',
                  }}
                />
              </div>

              {/* Event card */}
              <div className="mt-1 w-[184px] rounded-lg border border-cyber-300/40 bg-cyber-100/30 p-2.5">
                {n.is_origin && (
                  <div className="flex items-center gap-1 text-[9px] font-bold uppercase tracking-wider text-red-700 mb-1">
                    <Crosshair className="w-3 h-3" />
                    Origin
                  </div>
                )}
                <div
                  className="text-[10px] font-semibold uppercase tracking-wider mb-1"
                  style={{ color: stage.color }}
                >
                  {n.stage_label || stage.label}
                </div>
                <div
                  className="text-xs text-slate-900 font-medium break-words leading-snug"
                  title={n.source_label}
                >
                  {n.source_label}
                </div>
                <div className="mt-1.5 flex items-center gap-1.5 flex-wrap">
                  <span
                    className={`text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full border ${
                      SEVERITY_PILL[n.severity] || SEVERITY_PILL.info
                    }`}
                  >
                    {n.severity}
                  </span>
                  {n.source_count > 1 && (
                    <span className="text-[9px] text-slate-500 font-mono">
                      {n.source_count} copies
                    </span>
                  )}
                </div>
                {n.evidence_snippet && (
                  <p
                    className="mt-1.5 text-[10px] text-slate-500 leading-snug line-clamp-2"
                    title={n.evidence_snippet}
                  >
                    {n.evidence_snippet}
                  </p>
                )}
                {n.intelx_url && (
                  <a
                    href={n.intelx_url}
                    target="_blank"
                    rel="noreferrer"
                    className="mt-1.5 inline-flex items-center gap-1 text-[10px] text-shinobi-green hover:underline"
                  >
                    <ExternalLink className="w-3 h-3" />
                    intelx.io
                  </a>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default LeakTrace;
